/**
 * CalmSupportLevelEntry — gentle surfacing of the existing Support
 * Level capability on the calm learner dashboard.
 *
 * Operator brief (Activation Phase 1 #2, Feb 17, 2026 — AP-5
 * completion):
 *   "Reuse existing backend. Reuse existing frontend capability if
 *    present. No new emotional-support systems. No new collections.
 *    No new endpoints. No parallel UI systems.
 *    Calm home entry + MobileBottomSheet + same quiet/supportive
 *    tone as Red Button. Silence when not needed; silence after
 *    already completed today."
 *
 * Reuses existing GET /api/support-level/today and POST
 * /api/support-level (both already live in
 * `backend/routes/learner_flow.py`). The three option strings are
 * authored in DailyCheckIn.js — re-stating them here is acceptable
 * (operator brief explicitly permits "reuse existing frontend
 * capability if present"; DailyCheckIn renders them inline in a
 * multi-step flow that we are NOT remounting). They are also
 * tagged with the canonical level IDs that the backend validates.
 *
 * Doctrine adherence (per IMPLEMENTED_CAPABILITY_REGISTRY.md):
 *   • Zero new endpoints. Zero new collections. Zero new evidence.
 *   • Reuses canonical MobileBottomSheet primitive.
 *   • Calm-baseline silence on API error AND on already-selected.
 *   • Pairs with <CalmRedButtonEntry> for the AP-5 emotional-
 *     continuity surface.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Heart } from "lucide-react";
import { MobileBottomSheet } from "../mobile";

const API = process.env.REACT_APP_BACKEND_URL;

// Canonical IDs match backend validation in routes/learner_flow.py.
// Copy mirrors DailyCheckIn.js to keep the learner-facing wording
// consistent. If DailyCheckIn ever updates its copy, mirror here.
const SUPPORT_OPTIONS = [
  {
    id: "normal_challenge",
    label: "Push me today",
    desc: "I'm up for being stretched.",
  },
  {
    id: "steady_progress",
    label: "Steady — building confidence",
    desc: "I want to go at a calm pace and lock things in.",
  },
  {
    id: "extra_support",
    label: "Go gentle — I need support",
    desc: "A bit more time, patience, and clear steps today.",
  },
];


export default function CalmSupportLevelEntry() {
  const [today, setToday] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchToday = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/support-level/today`, {
        withCredentials: true,
      });
      setToday(r.data);
    } catch {
      setToday(null);
    }
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const select = useCallback(async (level) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/support-level`,
        { level },
        { withCredentials: true },
      );
      await fetchToday();
      setOpen(false);
    } catch {
      // Calm silence on failure — same pattern as RedButton.
    } finally {
      setBusy(false);
    }
  }, [fetchToday]);

  if (today === null) return null;             // API down → silent
  if (today.level) return null;                // already chosen → silent

  return (
    <>
      <button
        type="button"
        data-testid="calm-home-support-level-entry"
        onClick={() => setOpen(true)}
        className="w-full text-left text-xs text-slate-500 dark:text-slate-400
                   hover:text-slate-700 dark:hover:text-slate-200
                   inline-flex items-center gap-1.5 transition-colors py-1"
      >
        <Heart className="w-3.5 h-3.5" />
        <span>How would you like today to feel?</span>
      </button>
      <MobileBottomSheet
        open={open}
        onClose={() => (busy ? null : setOpen(false))}
        testid="calm-home-support-level-sheet"
        title="How do you want today to feel?"
        description="Pick the level that'll help you get the most out of today. Your instructor will shape the lesson around this."
      >
        <div className="space-y-2.5 p-1" data-testid="calm-home-support-level-options">
          {SUPPORT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              data-testid={`calm-home-support-${opt.id}`}
              disabled={busy}
              onClick={() => select(opt.id)}
              className="w-full p-4 rounded-2xl border-2 border-slate-200
                         hover:border-slate-300 dark:border-slate-700
                         dark:hover:border-slate-500 text-left transition-all
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <p className="font-medium text-sm text-slate-700 dark:text-slate-200">
                {opt.label}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {opt.desc}
              </p>
            </button>
          ))}
        </div>
      </MobileBottomSheet>
    </>
  );
}
